import { useState } from 'react';
import ExcelJS from 'exceljs';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logActivity } from './useActivityLog';

interface ImportResult {
  success: number;
  failed: number;
  errors: string[];
}

export const useExcelImport = () => {
  const [isImporting, setIsImporting] = useState(false);

  const parseExcelFile = async (file: File): Promise<any[]> => {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);
    
    const worksheet = workbook.worksheets[0];
    if (!worksheet) {
      throw new Error('No worksheet found in file');
    }

    const jsonData: any[] = [];
    const headers: string[] = [];

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) {
        // First row is headers
        row.eachCell((cell, colNumber) => {
          headers[colNumber - 1] = String(cell.value || '').toLowerCase().trim();
        });
      } else {
        // Data rows
        const rowData: any = {};
        row.eachCell((cell, colNumber) => {
          const header = headers[colNumber - 1];
          if (header) {
            rowData[header] = cell.value;
          }
        });
        if (Object.keys(rowData).length > 0) {
          jsonData.push(rowData);
        }
      }
    });

    return jsonData;
  };

  const importRoster = async (file: File, siteId: string): Promise<ImportResult> => {
    setIsImporting(true);
    const result: ImportResult = { success: 0, failed: 0, errors: [] };

    try {
      const data = await parseExcelFile(file);
      
      if (data.length === 0) {
        throw new Error('Excel file is empty');
      }

      // Validate required columns
      const requiredColumns = ['first_name', 'last_name', 'room_number'];
      const firstRow: any = data[0];
      const missingColumns = requiredColumns.filter(col => !(col in firstRow));
      
      if (missingColumns.length > 0) {
        throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
      }

      // Process each row
      for (let i = 0; i < data.length; i++) {
        const row: any = data[i];
        
        try {
          // Validate row data
          if (!row.first_name || !row.last_name) {
            result.errors.push(`Row ${i + 1}: Missing name`);
            result.failed++;
            continue;
          }

          // Find or create client
          let clientId: string;
          const { data: existingClient, error: searchError } = await supabase
            .from('clients')
            .select('id')
            .eq('first_name', String(row.first_name).trim())
            .eq('last_name', String(row.last_name).trim())
            .eq('site_id', siteId)
            .is('archived_at', null)
            .single();

          if (existingClient) {
            clientId = existingClient.id;
          } else {
            // Create new client
            const { data: newClient, error: createError } = await supabase
              .from('clients')
              .insert({
                first_name: String(row.first_name).trim(),
                last_name: String(row.last_name).trim(),
                site_id: siteId,
                status: 'unbedded',
                date_of_birth: row.date_of_birth || null,
                gender: row.gender ? String(row.gender) : null,
                phone: row.phone ? String(row.phone) : null,
                email: row.email ? String(row.email) : null
              })
              .select('id')
              .single();

            if (createError || !newClient) {
              result.errors.push(`Row ${i + 1}: Failed to create client - ${createError?.message}`);
              result.failed++;
              continue;
            }
            clientId = newClient.id;
          }

          // Assign room if provided
          if (row.room_number) {
            const { data: room, error: roomError } = await supabase
              .from('rooms')
              .select('id')
              .eq('room_number', String(row.room_number))
              .eq('site_id', siteId)
              .single();

            if (room) {
              await supabase
                .from('clients')
                .update({ room_id: room.id })
                .eq('id', clientId);
            } else {
              result.errors.push(`Row ${i + 1}: Room ${row.room_number} not found`);
            }
          }

          result.success++;
        } catch (rowError: any) {
          result.errors.push(`Row ${i + 1}: ${rowError.message}`);
          result.failed++;
        }
      }

      await logActivity({
        actionType: 'create_client',
        actionDetails: {
          import_type: 'excel_roster',
          total_rows: data.length,
          success: result.success,
          failed: result.failed
        },
        siteId
      });

      if (result.success > 0) {
        toast.success(`Imported ${result.success} clients successfully`);
      }
      if (result.failed > 0) {
        toast.error(`Failed to import ${result.failed} rows`);
      }

    } catch (error: any) {
      toast.error(error.message || 'Import failed');
      result.errors.push(error.message);
    } finally {
      setIsImporting(false);
    }

    return result;
  };

  const importDocuments = async (file: File, siteId: string): Promise<ImportResult> => {
    setIsImporting(true);
    const result: ImportResult = { success: 0, failed: 0, errors: [] };

    try {
      const data = await parseExcelFile(file);
      
      if (data.length === 0) {
        throw new Error('Excel file is empty');
      }

      // Validate required columns
      const requiredColumns = ['title', 'content'];
      const firstRow: any = data[0];
      const missingColumns = requiredColumns.filter(col => !(col in firstRow));
      
      if (missingColumns.length > 0) {
        throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Process each row
      for (let i = 0; i < data.length; i++) {
        const row: any = data[i];
        
        try {
          if (!row.title || !row.content) {
            result.errors.push(`Row ${i + 1}: Missing title or content`);
            result.failed++;
            continue;
          }

          // Find client if client_name provided
          let clientId: string | null = null;
          if (row.client_first_name && row.client_last_name) {
            const { data: client } = await supabase
              .from('clients')
              .select('id')
              .eq('first_name', String(row.client_first_name).trim())
              .eq('last_name', String(row.client_last_name).trim())
              .eq('site_id', siteId)
              .is('archived_at', null)
              .single();
            
            if (client) {
              clientId = client.id;
            }
          }

          // Create note/document
          const { error: createError } = await supabase
            .from('notes')
            .insert({
              title: String(row.title).trim(),
              content: String(row.content).trim(),
              site_id: siteId,
              client_id: clientId,
              created_by: user.id
            });

          if (createError) {
            result.errors.push(`Row ${i + 1}: ${createError.message}`);
            result.failed++;
            continue;
          }

          result.success++;
        } catch (rowError: any) {
          result.errors.push(`Row ${i + 1}: ${rowError.message}`);
          result.failed++;
        }
      }

      await logActivity({
        actionType: 'create_note',
        actionDetails: {
          import_type: 'excel_documents',
          total_rows: data.length,
          success: result.success,
          failed: result.failed
        },
        siteId
      });

      if (result.success > 0) {
        toast.success(`Imported ${result.success} documents successfully`);
      }
      if (result.failed > 0) {
        toast.error(`Failed to import ${result.failed} rows`);
      }

    } catch (error: any) {
      toast.error(error.message || 'Import failed');
      result.errors.push(error.message);
    } finally {
      setIsImporting(false);
    }

    return result;
  };

  const downloadTemplate = async (type: 'roster' | 'documents') => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Template');

    if (type === 'roster') {
      const headers = ['first_name', 'last_name', 'date_of_birth', 'gender', 'phone', 'email', 'room_number'];
      worksheet.addRow(headers);
      worksheet.addRow(['John', 'Doe', '1990-01-15', 'Male', '555-0100', 'john.doe@example.com', '101']);
    } else {
      const headers = ['title', 'content', 'client_first_name', 'client_last_name'];
      worksheet.addRow(headers);
      worksheet.addRow(['Sample Document', 'This is a sample document content', 'John', 'Doe']);
    }

    // Style header row
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };

    // Auto-width columns
    worksheet.columns.forEach(column => {
      let maxLength = 10;
      column.eachCell?.({ includeEmpty: true }, cell => {
        const cellLength = cell.value ? cell.value.toString().length : 0;
        if (cellLength > maxLength) {
          maxLength = cellLength;
        }
      });
      column.width = Math.min(maxLength + 2, 30);
    });

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${type}_template.xlsx`;
    link.click();
    
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  return {
    isImporting,
    importRoster,
    importDocuments,
    downloadTemplate
  };
};
